/**
 * PokerAI — AI opponents for Texas Hold'em Poker
 *
 * Implements three difficulty levels:
 *   Easy:   Hand-strength-only decisions, no bluffing
 *   Medium: Pot odds, position awareness, ~15% bluff rate
 *   Hard:   GTO-inspired balanced strategy with opponent tracking
 *
 * Requirements: 13.1–13.6, 14.1–14.7, 15.1–15.8
 */

import {
  Card,
  HandRank,
  HandResult,
  PlayerAction,
  ActionType,
  ValidAction,
  VisibleGameState,
  VisiblePlayerState,
  PokerDifficulty,
  ActionRecord,
  RANK_VALUES,
} from './types';
import { evaluate, findBestHand } from './HandEvaluator';

// ============================================================================
// Opponent Tracker (Hard AI only)
// ============================================================================

/** Per-opponent statistics tracked across hands */
export interface OpponentStats {
  handsObserved: number;
  vpipCount: number;        // Voluntarily put money in pot
  pfrCount: number;         // Pre-flop raise
  raiseCount: number;
  betCount: number;
  callCount: number;
  foldToRaiseCount: number;
  facedRaiseCount: number;
  showdownCount: number;
}

export class OpponentTracker {
  private stats: Map<string, OpponentStats> = new Map();

  /** Get or create stats for a player */
  getStats(playerId: string): OpponentStats {
    if (!this.stats.has(playerId)) {
      this.stats.set(playerId, {
        handsObserved: 0,
        vpipCount: 0,
        pfrCount: 0,
        raiseCount: 0,
        betCount: 0,
        callCount: 0,
        foldToRaiseCount: 0,
        facedRaiseCount: 0,
        showdownCount: 0,
      });
    }
    return this.stats.get(playerId)!;
  }

  /** Record actions from a completed hand */
  recordHandActions(actions: ActionRecord[], showdownPlayerIds: string[]): void {
    const playerActions = new Map<string, ActionRecord[]>();
    for (const action of actions) {
      if (!playerActions.has(action.playerId)) {
        playerActions.set(action.playerId, []);
      }
      playerActions.get(action.playerId)!.push(action);
    }

    for (const [playerId, pActions] of Array.from(playerActions.entries())) {
      const stats = this.getStats(playerId);
      stats.handsObserved++;

      const preFlopActions = pActions.filter((a: any) => a.phase === 'pre-flop');
      const hasVoluntaryAction = pActions.some(
        (a: any) => a.action === 'call' || a.action === 'raise' || a.action === 'all-in'
      );
      if (hasVoluntaryAction) stats.vpipCount++;

      const hasPFR = preFlopActions.some((a: any) => a.action === 'raise' || a.action === 'all-in');
      if (hasPFR) stats.pfrCount++;

      for (const a of pActions) {
        if ((a as any).action === 'raise' || (a as any).action === 'all-in') stats.raiseCount++;
        if ((a as any).action === 'call') stats.callCount++;
      }

      if (showdownPlayerIds.includes(playerId)) stats.showdownCount++;
    }
  }

  /** Record that a player folded to a raise */
  recordFoldToRaise(playerId: string): void {
    const stats = this.getStats(playerId);
    stats.foldToRaiseCount++;
    stats.facedRaiseCount++;
  }

  /** Record that a player faced a raise but did not fold */
  recordFacedRaise(playerId: string): void {
    const stats = this.getStats(playerId);
    stats.facedRaiseCount++;
  }

  /** VPIP percentage (0-1) */
  getVPIP(playerId: string): number {
    const s = this.getStats(playerId);
    return s.handsObserved > 0 ? s.vpipCount / s.handsObserved : 0.5;
  }

  /** PFR percentage (0-1) */
  getPFR(playerId: string): number {
    const s = this.getStats(playerId);
    return s.handsObserved > 0 ? s.pfrCount / s.handsObserved : 0.2;
  }

  /** Aggression factor: (raises + bets) / calls. Higher = more aggressive */
  getAggressionFactor(playerId: string): number {
    const s = this.getStats(playerId);
    if (s.callCount === 0) return s.raiseCount + s.betCount > 0 ? 3.0 : 1.0;
    return (s.raiseCount + s.betCount) / s.callCount;
  }

  /** Fold to raise percentage (0-1) */
  getFoldToRaise(playerId: string): number {
    const s = this.getStats(playerId);
    return s.facedRaiseCount > 0 ? s.foldToRaiseCount / s.facedRaiseCount : 0.4;
  }

  /** Showdown frequency (0-1) */
  getShowdownFrequency(playerId: string): number {
    const s = this.getStats(playerId);
    return s.handsObserved > 0 ? s.showdownCount / s.handsObserved : 0.3;
  }

  /** Reset all tracked data */
  reset(): void {
    this.stats.clear();
  }
}

// ============================================================================
// Helper functions
// ============================================================================

/** Evaluate hand strength from hole cards + community cards */
function evaluateHandStrength(
  holeCards: Card[],
  communityCards: Card[]
): HandResult | null {
  const allCards = [...holeCards, ...communityCards];
  if (allCards.length < 5) {
    // Pre-flop: evaluate just the 2 hole cards heuristically
    return null;
  }
  return findBestHand(holeCards, communityCards);
}

/** Get a numeric hand strength score (0-9 = HandRank) */
function getHandRankScore(holeCards: Card[], communityCards: Card[]): number {
  const result = evaluateHandStrength(holeCards, communityCards);
  if (!result) {
    // Pre-flop: use hole card strength heuristic
    return preFlopHandStrength(holeCards);
  }
  return result.rank;
}

/**
 * Pre-flop hand strength heuristic (0-9 scale).
 * Pocket pairs, suited connectors, high cards get higher scores.
 */
function preFlopHandStrength(holeCards: Card[]): number {
  if (holeCards.length < 2) return 0;
  const [c1, c2] = holeCards;
  const v1 = RANK_VALUES[c1.rank];
  const v2 = RANK_VALUES[c2.rank];
  const high = Math.max(v1, v2);
  const low = Math.min(v1, v2);
  const suited = c1.suit === c2.suit;
  const paired = v1 === v2;
  const gap = high - low;

  if (paired) {
    // Pocket pairs: AA=9, KK=8, QQ=7, ... 22=2
    return Math.min(9, Math.floor(high / 2) + 2);
  }

  let score = 0;
  // High card value contribution
  score += high >= 14 ? 3 : high >= 13 ? 2.5 : high >= 12 ? 2 : high >= 10 ? 1.5 : 1;
  // Second card contribution
  score += low >= 12 ? 1.5 : low >= 10 ? 1 : 0.5;
  // Suited bonus
  if (suited) score += 0.5;
  // Connectedness bonus
  if (gap <= 1) score += 0.5;
  else if (gap <= 2) score += 0.25;

  return Math.min(9, Math.max(0, Math.round(score)));
}

/** Determine table position: 'early', 'middle', or 'late' */
function getTablePosition(
  seatIndex: number,
  dealerSeatIndex: number,
  activePlayers: VisiblePlayerState[]
): 'early' | 'middle' | 'late' {
  // Order players clockwise from dealer
  const sorted = [...activePlayers].sort((a, b) => {
    const posA = (a.seatIndex - dealerSeatIndex + 8) % 8;
    const posB = (b.seatIndex - dealerSeatIndex + 8) % 8;
    return posA - posB;
  });

  const playerPos = sorted.findIndex(p => p.seatIndex === seatIndex);
  const total = sorted.length;

  if (total <= 3) {
    // With 2-3 players, dealer/button is late, others are early
    return playerPos >= total - 1 ? 'late' : 'early';
  }

  const third = total / 3;
  if (playerPos < third) return 'early';
  if (playerPos < third * 2) return 'middle';
  return 'late';
}

/** Calculate total pot from pots array */
function getTotalPot(gameState: VisibleGameState): number {
  const potTotal = gameState.pots.reduce((sum, p) => sum + p.amount, 0);
  // Also add current round bets not yet collected into pots
  const currentBets = gameState.players.reduce((sum, p) => sum + p.currentBet, 0);
  return potTotal + currentBets;
}

/** Calculate pot odds: amount to call / (pot + amount to call) */
function calculatePotOdds(amountToCall: number, pot: number): number {
  if (amountToCall <= 0) return 0;
  return amountToCall / (pot + amountToCall);
}

/** Find a valid action of a given type */
function findValidAction(validActions: ValidAction[], type: ActionType): ValidAction | undefined {
  return validActions.find(a => a.type === type);
}

/** Check if an action type is available */
function hasAction(validActions: ValidAction[], type: ActionType): boolean {
  return validActions.some(a => a.type === type);
}

/** Clamp a value between min and max */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Create a raise action with the given amount, clamped to valid range */
function createRaiseAction(validActions: ValidAction[], amount: number): PlayerAction | null {
  const raiseAction = findValidAction(validActions, 'raise');
  if (!raiseAction || raiseAction.minAmount === undefined || raiseAction.maxAmount === undefined) {
    return null;
  }
  const clampedAmount = clamp(Math.round(amount), raiseAction.minAmount, raiseAction.maxAmount);
  return { type: 'raise', amount: clampedAmount };
}

/** Random number in range [min, max] */
function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Create a delay promise */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============================================================================
// PokerAI Class
// ============================================================================

export class PokerAI {
  difficulty: PokerDifficulty;
  private opponentTracker: OpponentTracker;

  /** When true, skip the artificial delay (for testing) */
  testMode: boolean = false;

  constructor(difficulty: PokerDifficulty = 'easy') {
    this.difficulty = difficulty;
    this.opponentTracker = new OpponentTracker();
  }

  /** Get the AI's betting decision */
  async getAction(
    gameState: VisibleGameState,
    playerId: string,
    validActions: ValidAction[]
  ): Promise<PlayerAction> {
    if (validActions.length === 0) {
      return { type: 'fold' };
    }

    // If only one action available, return it
    if (validActions.length === 1) {
      const only = validActions[0];
      return this.actionFromValid(only);
    }

    // Simulate thinking delay
    if (!this.testMode) {
      const delayMs = this.getThinkingDelay();
      await delay(delayMs);
    }

    let action: PlayerAction;

    switch (this.difficulty) {
      case 'easy':
        action = this.getEasyAction(gameState, playerId, validActions);
        break;
      case 'medium':
        action = this.getMediumAction(gameState, playerId, validActions);
        break;
      case 'hard':
        action = this.getHardAction(gameState, playerId, validActions);
        break;
      default:
        action = this.getEasyAction(gameState, playerId, validActions);
    }

    // Ensure the action is valid
    if (!hasAction(validActions, action.type)) {
      // Fallback: check > call > fold
      if (hasAction(validActions, 'check')) return { type: 'check' };
      if (hasAction(validActions, 'call')) return { type: 'call' };
      return { type: 'fold' };
    }

    return action;
  }

  /** Set difficulty level */
  setDifficulty(difficulty: PokerDifficulty): void {
    this.difficulty = difficulty;
  }

  /** Reset AI state (clears opponent tracking) */
  reset(): void {
    this.opponentTracker.reset();
  }

  /** Get the opponent tracker (for testing / external access) */
  getOpponentTracker(): OpponentTracker {
    return this.opponentTracker;
  }

  // ========================================================================
  // Easy AI — Hand strength only, no bluffing
  // ========================================================================

  private getEasyAction(
    gameState: VisibleGameState,
    playerId: string,
    validActions: ValidAction[]
  ): PlayerAction {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return { type: 'fold' };

    const handStrength = getHandRankScore(player.holeCards, gameState.communityCards);

    // Strong hand (Two Pair or better): raise with min-raise
    if (handStrength >= HandRank.TWO_PAIR) {
      const raiseAction = createRaiseAction(validActions, findValidAction(validActions, 'raise')?.minAmount || 0);
      if (raiseAction) return raiseAction;
      if (hasAction(validActions, 'call')) return { type: 'call' };
      if (hasAction(validActions, 'check')) return { type: 'check' };
    }

    // Moderate hand (One Pair or better): call
    if (handStrength >= HandRank.ONE_PAIR) {
      if (hasAction(validActions, 'call')) return { type: 'call' };
      if (hasAction(validActions, 'check')) return { type: 'check' };
    }

    // Weak hand (High Card): fold if there's a bet, check if free
    if (hasAction(validActions, 'check')) return { type: 'check' };
    return { type: 'fold' };
  }

  // ========================================================================
  // Medium AI — Pot odds, position, ~15% bluff rate
  // ========================================================================

  private getMediumAction(
    gameState: VisibleGameState,
    playerId: string,
    validActions: ValidAction[]
  ): PlayerAction {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return { type: 'fold' };

    const handStrength = getHandRankScore(player.holeCards, gameState.communityCards);
    const activePlayers = gameState.players.filter(
      p => p.status === 'active' || p.status === 'all-in'
    );
    const position = getTablePosition(
      player.seatIndex,
      gameState.dealerSeatIndex,
      activePlayers
    );
    const pot = getTotalPot(gameState);
    const callAction = findValidAction(validActions, 'call');
    const amountToCall = callAction?.callAmount || 0;
    const potOdds = calculatePotOdds(amountToCall, pot);
    const bluffRoll = Math.random();

    // Strong hand in late position: raise 50-75% of pot
    if (handStrength >= HandRank.THREE_OF_A_KIND) {
      const raiseAmount = pot * randomInRange(0.5, 0.75);
      const raiseAction = createRaiseAction(validActions, raiseAmount);
      if (raiseAction) return raiseAction;
      if (hasAction(validActions, 'call')) return { type: 'call' };
      if (hasAction(validActions, 'check')) return { type: 'check' };
    }

    // Good hand: raise in late position, call otherwise
    if (handStrength >= HandRank.TWO_PAIR) {
      if (position === 'late') {
        const raiseAmount = pot * randomInRange(0.5, 0.75);
        const raiseAction = createRaiseAction(validActions, raiseAmount);
        if (raiseAction) return raiseAction;
      }
      if (hasAction(validActions, 'call')) return { type: 'call' };
      if (hasAction(validActions, 'check')) return { type: 'check' };
    }

    // Moderate hand: call if pot odds are favorable
    if (handStrength >= HandRank.ONE_PAIR) {
      // Favorable pot odds: need to win less often than our equity
      if (amountToCall === 0) {
        if (hasAction(validActions, 'check')) return { type: 'check' };
      }
      if (potOdds < 0.35) {
        if (hasAction(validActions, 'call')) return { type: 'call' };
      }
      if (hasAction(validActions, 'check')) return { type: 'check' };
      return { type: 'fold' };
    }

    // Bluff: ~15% in late position with weak holdings
    if (bluffRoll < 0.15 && position === 'late' && handStrength < HandRank.ONE_PAIR) {
      const raiseAmount = pot * 0.5;
      const raiseAction = createRaiseAction(validActions, raiseAmount);
      if (raiseAction) return raiseAction;
    }

    // Weak hand: check if free, fold otherwise
    if (hasAction(validActions, 'check')) return { type: 'check' };

    // Very favorable pot odds might justify a call even with weak hand
    if (potOdds < 0.15 && hasAction(validActions, 'call')) {
      return { type: 'call' };
    }

    return { type: 'fold' };
  }

  // ========================================================================
  // Hard AI — GTO-inspired with opponent tracking
  // ========================================================================

  private getHardAction(
    gameState: VisibleGameState,
    playerId: string,
    validActions: ValidAction[]
  ): PlayerAction {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return { type: 'fold' };

    const handStrength = getHandRankScore(player.holeCards, gameState.communityCards);
    const activePlayers = gameState.players.filter(
      p => p.status === 'active' || p.status === 'all-in'
    );
    const position = getTablePosition(
      player.seatIndex,
      gameState.dealerSeatIndex,
      activePlayers
    );
    const pot = getTotalPot(gameState);
    const callAction = findValidAction(validActions, 'call');
    const amountToCall = callAction?.callAmount || 0;
    const potOdds = calculatePotOdds(amountToCall, pot);

    // Stack-to-pot ratio
    const effectiveStack = player.chipStack;
    const spr = pot > 0 ? effectiveStack / pot : 20;

    // Opponent tendencies (average across opponents)
    const opponents = activePlayers.filter(p => p.id !== playerId);
    const avgFoldToRaise = this.getAverageOpponentStat(opponents, 'foldToRaise');
    const avgAggression = this.getAverageOpponentStat(opponents, 'aggression');
    const avgVPIP = this.getAverageOpponentStat(opponents, 'vpip');

    // Implied odds: better with deep stacks and passive opponents
    const impliedOddsMultiplier = spr > 10 ? 1.5 : spr > 5 ? 1.2 : 1.0;
    const adjustedPotOdds = potOdds / impliedOddsMultiplier;

    // GTO-inspired value range: hands strong enough to bet for value
    const isValueHand = this.isInValueRange(handStrength, gameState.communityCards, position);

    // GTO-inspired bluff range: balanced bluffing
    const isBluffCandidate = this.isInBluffRange(
      handStrength, position, spr, avgFoldToRaise
    );

    // Premium hands: always raise/re-raise
    if (handStrength >= HandRank.FULL_HOUSE) {
      // Large bet for polarized range with very strong hands
      const betSize = this.calculateBetSize(pot, spr, 'polarized', avgAggression);
      const raiseAction = createRaiseAction(validActions, betSize);
      if (raiseAction) return raiseAction;
      if (hasAction(validActions, 'call')) return { type: 'call' };
      if (hasAction(validActions, 'check')) return { type: 'check' };
    }

    // Value hands: bet/raise with strategic sizing
    if (isValueHand) {
      // Thin value: smaller bets; strong value: larger bets
      const sizingType = handStrength >= HandRank.THREE_OF_A_KIND ? 'polarized' : 'thin';
      const betSize = this.calculateBetSize(pot, spr, sizingType, avgAggression);
      const raiseAction = createRaiseAction(validActions, betSize);
      if (raiseAction) return raiseAction;
      if (hasAction(validActions, 'call')) return { type: 'call' };
      if (hasAction(validActions, 'check')) return { type: 'check' };
    }

    // Bluff candidates: raise as a bluff at balanced frequency
    if (isBluffCandidate) {
      const betSize = this.calculateBetSize(pot, spr, 'bluff', avgAggression);
      const raiseAction = createRaiseAction(validActions, betSize);
      if (raiseAction) return raiseAction;
    }

    // Drawing hands / marginal: consider pot odds + implied odds
    if (handStrength >= HandRank.ONE_PAIR || adjustedPotOdds < 0.25) {
      if (amountToCall === 0 && hasAction(validActions, 'check')) return { type: 'check' };
      if (adjustedPotOdds < 0.3 && hasAction(validActions, 'call')) return { type: 'call' };
      if (hasAction(validActions, 'check')) return { type: 'check' };
    }

    // Weak hand: check if free
    if (hasAction(validActions, 'check')) return { type: 'check' };

    // Very favorable pot odds
    if (adjustedPotOdds < 0.12 && hasAction(validActions, 'call')) {
      return { type: 'call' };
    }

    return { type: 'fold' };
  }

  /** Determine if hand is in the value betting range */
  private isInValueRange(
    handStrength: number,
    communityCards: Card[],
    position: 'early' | 'middle' | 'late'
  ): boolean {
    // Tighter value range in early position, wider in late
    const threshold = position === 'early'
      ? HandRank.THREE_OF_A_KIND
      : position === 'middle'
        ? HandRank.TWO_PAIR
        : HandRank.ONE_PAIR + 0.5; // Strong one pair in late position

    return handStrength >= threshold;
  }

  /** Determine if hand is a bluff candidate (GTO-balanced) */
  private isInBluffRange(
    handStrength: number,
    position: 'early' | 'middle' | 'late',
    spr: number,
    opponentFoldToRaise: number
  ): boolean {
    // Only bluff with weak hands (not medium-strength hands)
    if (handStrength >= HandRank.ONE_PAIR) return false;

    // Bluff more in late position
    let bluffFrequency: number;
    if (position === 'late') {
      bluffFrequency = 0.25;
    } else if (position === 'middle') {
      bluffFrequency = 0.12;
    } else {
      bluffFrequency = 0.05;
    }

    // Bluff more against players who fold often
    if (opponentFoldToRaise > 0.5) {
      bluffFrequency *= 1.3;
    }

    // Bluff less with shallow SPR (committed to pot)
    if (spr < 3) {
      bluffFrequency *= 0.5;
    }

    return Math.random() < bluffFrequency;
  }

  /** Calculate optimal bet size based on strategy type */
  private calculateBetSize(
    pot: number,
    spr: number,
    type: 'thin' | 'polarized' | 'bluff',
    opponentAggression: number
  ): number {
    let sizeFraction: number;

    switch (type) {
      case 'thin':
        // Small bets for thin value: 25-40% pot
        sizeFraction = randomInRange(0.25, 0.4);
        break;
      case 'polarized':
        // Large bets for polarized ranges: 66-100% pot
        sizeFraction = randomInRange(0.66, 1.0);
        break;
      case 'bluff':
        // Bluff sizing: match value bet sizing for balance (50-75% pot)
        sizeFraction = randomInRange(0.5, 0.75);
        break;
      default:
        sizeFraction = 0.5;
    }

    // Adjust for SPR: bet smaller with shallow stacks
    if (spr < 4) {
      sizeFraction *= 0.7;
    }

    // Adjust against passive opponents: bet larger for value
    if (opponentAggression < 1.0 && type !== 'bluff') {
      sizeFraction *= 1.2;
    }

    return Math.max(1, Math.round(pot * sizeFraction));
  }

  /** Get average opponent stat */
  private getAverageOpponentStat(
    opponents: VisiblePlayerState[],
    stat: 'foldToRaise' | 'aggression' | 'vpip'
  ): number {
    if (opponents.length === 0) return stat === 'aggression' ? 1.0 : 0.4;

    let total = 0;
    for (const opp of opponents) {
      switch (stat) {
        case 'foldToRaise':
          total += this.opponentTracker.getFoldToRaise(opp.id);
          break;
        case 'aggression':
          total += this.opponentTracker.getAggressionFactor(opp.id);
          break;
        case 'vpip':
          total += this.opponentTracker.getVPIP(opp.id);
          break;
      }
    }
    return total / opponents.length;
  }

  // ========================================================================
  // Utility
  // ========================================================================

  /** Get thinking delay range based on difficulty */
  private getThinkingDelay(): number {
    switch (this.difficulty) {
      case 'easy':
        return randomInRange(500, 1500);
      case 'medium':
        return randomInRange(1000, 2500);
      case 'hard':
        return randomInRange(1500, 3000);
      default:
        return randomInRange(500, 1500);
    }
  }

  /** Convert a ValidAction to a PlayerAction */
  private actionFromValid(validAction: ValidAction): PlayerAction {
    switch (validAction.type) {
      case 'raise':
        return { type: 'raise', amount: validAction.minAmount };
      case 'all-in':
        return { type: 'all-in' };
      default:
        return { type: validAction.type };
    }
  }
}
